import React, { useState } from 'react';
import { TrashIcon, Bars3Icon, PlusIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

const LineItemsManager = ({ lineItems, setLineItems, currency = 'USD' }) => {
  const [expandedItems, setExpandedItems] = useState(new Set());

  const updateLineItem = (id, field, value) => {
    // Prevent leading zeros for numeric fields (except for decimal values)
    if ((field === 'quantity' || field === 'rate') && typeof value === 'string') {
      // Allow empty string
      if (value === '') {
        // Set empty value
      } else if (value === '0') {
        // Allow single zero
      } else if (value.startsWith('0') && !value.startsWith('0.')) {
        // Remove leading zeros except for decimal values
        value = value.replace(/^0+/, '') || '0';
      }
    }

    const updatedItems = lineItems.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        
        if (field === 'quantity' || field === 'rate') {
          // Handle empty strings and calculate amount
          const quantity = updatedItem.quantity === '' ? 0 : parseFloat(updatedItem.quantity) || 0;
          const rate = updatedItem.rate === '' ? 0 : parseFloat(updatedItem.rate) || 0;
          updatedItem.amount = quantity * rate;
        }
        
        return updatedItem;
      }
      return item;
    });
    setLineItems(updatedItems);
  };

  const removeLineItem = (id) => {
    setLineItems(lineItems.filter(item => item.id !== id));
    // Remove from expanded items if it was expanded
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  };

  // Sub-items management functions
  const toggleSubItems = (itemId) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
        // Initialize sub-items array if it doesn't exist
        if (!lineItems.find(item => item.id === itemId)?.subItems) {
          const updatedItems = lineItems.map(item => 
            item.id === itemId 
              ? { ...item, subItems: [] }
              : item
          );
          setLineItems(updatedItems);
        }
      }
      return newSet;
    });
  };

  const addSubItem = (itemId) => {
    const updatedItems = lineItems.map(item => {
      if (item.id === itemId) {
        const subItems = item.subItems || [];
        return {
          ...item,
          subItems: [...subItems, { id: Date.now().toString(), text: '' }]
        };
      }
      return item;
    });
    setLineItems(updatedItems);
  };

  const updateSubItem = (itemId, subItemId, text) => {
    const updatedItems = lineItems.map(item => {
      if (item.id === itemId) {
        const subItems = (item.subItems || []).map(subItem =>
          subItem.id === subItemId ? { ...subItem, text } : subItem
        );
        return { ...item, subItems };
      }
      return item;
    });
    setLineItems(updatedItems);
  };

  const removeSubItem = (itemId, subItemId) => {
    const updatedItems = lineItems.map(item => {
      if (item.id === itemId) {
        const subItems = (item.subItems || []).filter(subItem => subItem.id !== subItemId);
        return { ...item, subItems };
      }
      return item;
    });
    setLineItems(updatedItems);
  };

  const subtotal = lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Line Items</h3>
          <p className="text-sm text-gray-500 dark:text-[#666666] mt-1">
            Add multiple services and products to your invoice
          </p>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-bold ${
            subtotal < 0 
              ? 'text-red-600 dark:text-red-400' 
              : 'text-gray-900 dark:text-white'
          }`}>
            {currency} {subtotal.toFixed(2)}
          </div>
          <div className="text-sm text-gray-500 dark:text-[#666666]">Subtotal</div>
          {subtotal < 0 && (
            <div className="text-xs text-red-600 dark:text-red-400 mt-1 font-medium">
              ⚠️ Negative total - customer owes nothing
            </div>
          )}
        </div>
      </div>
      
      {/* Line Items List */}
      <div className="space-y-3">
        <AnimatePresence>
          {lineItems.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="group relative bg-white dark:bg-black border border-gray-200 dark:border-[#1A1A1A] rounded-2xl p-6 hover:border-gray-300 dark:hover:border-[#2A2A2A] transition-all duration-200"
            >
              {/* Drag handle */}
              <div className="absolute left-2 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Bars3Icon className="w-4 h-4 text-gray-400 dark:text-[#666666]" />
              </div>

              <div className="grid grid-cols-12 gap-4 items-start pl-6">
                {/* Description */}
                <div className="col-span-12 sm:col-span-5">
                  <label className="block text-xs font-medium text-gray-900 dark:text-white mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                    className="w-full bg-transparent border-0 text-base text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:ring-0 p-0 font-medium"
                    placeholder="Enter service description..."
                    autoComplete="off"
                    autoFocus={!item.description && index === lineItems.length - 1}
                  />
                </div>
                
                {/* Quantity */}
                <div className="col-span-4 sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-900 dark:text-white mb-2">
                    Qty
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={item.quantity || ''}
                    onChange={(e) => updateLineItem(item.id, 'quantity', e.target.value)}
                    onFocus={(e) => {
                      // Select all text when focused, making it easy to replace 0
                      e.target.select();
                    }}
                    onWheel={(e) => {
                      // Prevent mouse wheel from changing value
                      e.preventDefault();
                      e.target.blur();
                    }}
                    className="w-full bg-gray-50 dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white text-center focus:ring-2 focus:ring-brand/20 focus:border-brand hover:border-gray-300 dark:hover:border-[#666666] transition-all font-medium [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    placeholder="1"
                    autoComplete="off"
                  />
                </div>
                
                {/* Rate */}
                <div className="col-span-4 sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-900 dark:text-white mb-2">
                    Rate
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                      <span className="text-gray-500 dark:text-[#666666] text-xs font-medium">{currency}</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={item.rate || ''}
                      onChange={(e) => updateLineItem(item.id, 'rate', e.target.value)}
                      onFocus={(e) => {
                        // Select all text when focused, making it easy to replace 0
                        e.target.select();
                      }}
                      onWheel={(e) => {
                        // Prevent mouse wheel from changing value
                        e.preventDefault();
                        e.target.blur();
                      }}
                      className="w-full bg-gray-50 dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-lg pl-12 pr-3 py-2 text-sm text-gray-900 dark:text-white text-right focus:ring-2 focus:ring-brand/20 focus:border-brand hover:border-gray-300 dark:hover:border-[#666666] transition-all font-medium [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      placeholder="0.00"
                      autoComplete="off"
                    />
                  </div>
                </div>
                
                {/* Amount */}
                <div className="col-span-3 sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-900 dark:text-white mb-2">
                    Amount
                  </label>
                  <div className="text-right">
                    {(item.amount || 0) < 0 && (
                      <div className="mb-1">
                        <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded-md font-medium">
                          DISCOUNT
                        </span>
                      </div>
                    )}
                    <div className={`text-lg font-semibold ${
                      (item.amount || 0) < 0 
                        ? 'text-red-600 dark:text-red-400' 
                        : 'text-gray-900 dark:text-white'
                    }`}>
                      {currency} {(item.amount || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
                
                {/* Delete Button */}
                <div className="col-span-1 flex justify-end">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => removeLineItem(item.id)}
                    className="w-8 h-8 rounded-full bg-red-50 dark:bg-red-900/30 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition-all duration-200 flex items-center justify-center mt-6 opacity-0 group-hover:opacity-100"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </motion.button>
                </div>
              </div>

              {/* Add Details Button */}
              <div className="mt-3 pl-6">
                <motion.button
                  onClick={() => toggleSubItems(item.id)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-[#666666] hover:text-brand dark:hover:text-brand transition-colors rounded-lg hover:bg-gray-50 dark:hover:bg-[#111111]"
                >
                  {expandedItems.has(item.id) ? (
                    <ChevronDownIcon className="w-4 h-4" />
                  ) : (
                    <ChevronRightIcon className="w-4 h-4" />
                  )}
                  <span className="font-medium">
                    {expandedItems.has(item.id) ? 'Hide Details' : 'Add Details'}
                  </span>
                  {item.subItems && item.subItems.length > 0 && (
                    <span className="px-1.5 py-0.5 bg-brand/20 text-brand text-xs rounded-full font-medium">
                      {item.subItems.length}
                    </span>
                  )}
                </motion.button>
              </div>

              {/* Sub-items Section */}
              <AnimatePresence>
                {expandedItems.has(item.id) && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="mt-3 pl-6 border-l-2 border-brand/20"
                  >
                    <div className="pl-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-medium text-gray-900 dark:text-white uppercase tracking-wide">
                          Item Details
                        </h4>
                        <motion.button
                          onClick={() => addSubItem(item.id)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="flex items-center gap-1 px-2 py-1 text-xs text-brand hover:text-brand-hover font-medium rounded-md hover:bg-brand/10 transition-colors"
                        >
                          <PlusIcon className="w-3 h-3" />
                          Add Detail
                        </motion.button>
                      </div>
                      
                      {/* Sub-items List */}
                      <AnimatePresence>
                        {(item.subItems || []).map((subItem, subIndex) => (
                          <motion.div
                            key={subItem.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{ duration: 0.15, delay: subIndex * 0.05 }}
                            className="flex items-center gap-2 group/sub"
                          >
                            <div className="flex-shrink-0 w-1 h-1 bg-brand rounded-full mt-2"></div>
                            <input
                              type="text"
                              value={subItem.text}
                              onChange={(e) => updateSubItem(item.id, subItem.id, e.target.value)}
                              placeholder="Enter detail (e.g., 2 cameras, 3 years usage)"
                              className="flex-1 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#1A1A1A] rounded-lg px-3 py-1.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:ring-2 focus:ring-brand/20 focus:border-brand hover:border-gray-300 dark:hover:border-[#333333] transition-all"
                              autoComplete="off"
                              autoFocus={!subItem.text && subIndex === (item.subItems || []).length - 1}
                            />
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => removeSubItem(item.id, subItem.id)}
                              className="flex-shrink-0 w-6 h-6 rounded-full bg-red-50 dark:bg-red-900/30 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition-all duration-200 flex items-center justify-center opacity-0 group-hover/sub:opacity-100"
                            >
                              <TrashIcon className="w-3 h-3" />
                            </motion.button>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                      
                      {/* Add first sub-item message */}
                      {(!item.subItems || item.subItems.length === 0) && (
                        <div className="py-3 text-center">
                          <p className="text-xs text-gray-500 dark:text-[#666666] italic">
                            Add details to provide more information about this line item
                          </p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LineItemsManager; 